/**
 * IndexedDB 缓存工具 - 仅存储当前选中的头像
 * 支持存储 10MB+ 的大图片，减少服务器请求
 */

const DB_NAME = 'SeedreamAvatarCache';
const DB_VERSION = 1;
const STORE_NAME = 'avatars';
const SELECTED_KEY = 'current_selected'; // 固定 key，只存当前选中的

class AvatarCache {
  constructor() {
    this.db = null;
    this.dbPromise = this.initDB();
  }

  /**
   * 初始化 IndexedDB
   */
  initDB() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        console.warn('IndexedDB not supported, falling back to no cache');
        resolve(null);
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        resolve(null);
      };
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME);
        }

        // 创建对象仓库，使用固定 key
        db.createObjectStore(STORE_NAME, {
          keyPath: 'cacheKey'
        });
      };
    });
  }

  /**
   * 确保数据库连接
   */
  async ensureDB() {
    await this.dbPromise;
    return this.db;
  }

  /**
   * 保存当前选中的头像（覆盖之前的）
   * @param {Object} avatar - 头像对象 { id, avatar_url, avatar_base64, updatedAt }
   */
  async saveSelectedAvatar(avatar) {
    const db = await this.ensureDB();
    if (!db) return false;
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const cacheData = {
          cacheKey: SELECTED_KEY,
          avatarId: avatar.id,
          avatarUrl: avatar.avatar_url,
          avatarBase64: avatar.avatar_base64,
          updatedAt: Date.now()
        };
        const request = store.put(cacheData);
        request.onsuccess = () => {
          console.log('Selected avatar cached successfully:', avatar.id);
          resolve(true);
        };
        request.onerror = () => {
          console.error('Failed to cache selected avatar:', request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('Error caching selected avatar:', error);
        reject(error);
      }
    });
  }

  /**
   * 获取当前选中的头像
   * @returns {Object|null} - { id, avatar_url, avatar_base64 } or null
   */
  async getSelectedAvatar() {
    const db = await this.ensureDB();
    if (!db) return null;
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(SELECTED_KEY);
        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            resolve({
              id: result.avatarId,
              avatar_url: result.avatarUrl,
              avatar_base64: result.avatarBase64,
              updatedAt: result.updatedAt
            });
          } else {
            resolve(null);
          }
        };
        request.onerror = () => {
          console.error('Failed to get selected avatar from cache:', request.error);
          resolve(null);
        };
      } catch (error) {
        console.error('Error getting selected avatar from cache:', error);
        resolve(null);
      }
    });
  }

  /**
   * 删除缓存的头像
   */
  async deleteSelectedAvatar() {
    const db = await this.ensureDB();
    if (!db) return false;
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(SELECTED_KEY);
        request.onsuccess = () => {
          console.log('Selected avatar deleted from cache');
          resolve(true);
        };
        request.onerror = () => {
          console.error('Failed to delete selected avatar from cache:', request.error);
          resolve(false);
        };
      } catch (error) {
        console.error('Error deleting selected avatar from cache:', error);
        resolve(false);
      }
    });
  }

  /**
   * 从 URL 下载并缓存当前选中的头像
   * @param {string} url - 图片 URL
   * @param {number|string} avatarId - 头像 ID
   */
  async downloadAndCacheImage(url, avatarId) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onload = async () => {
          const base64 = reader.result;

          // 保存到缓存
          await this.saveSelectedAvatar({
            id: avatarId,
            avatar_url: url,
            avatar_base64: base64
          });
          resolve(base64);
        };
        reader.onerror = () => {
          console.error('Failed to read blob as base64');
          resolve(null);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Failed to download and cache image:', error);
      return null;
    }
  }

  /**
   * 清除所有缓存
   */
  async clearCache() {
    const db = await this.ensureDB();
    if (!db) return false;
    return new Promise((resolve, reject) => {
      try {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();
        request.onsuccess = () => {
          console.log('Cache cleared successfully');
          resolve(true);
        };
        request.onerror = () => {
          console.error('Failed to clear cache:', request.error);
          resolve(false);
        };
      } catch (error) {
        console.error('Error clearing cache:', error);
        resolve(false);
      }
    });
  }

  /**
   * 获取缓存统计信息
   */
  async getCacheStats() {
    const avatar = await this.getSelectedAvatar();
    let totalSize = 0;
    if (avatar && avatar.avatar_base64) {
      totalSize = avatar.avatar_base64.length * 0.75;
    }
    return {
      count: avatar ? 1 : 0,
      totalSize: totalSize,
      totalSizeFormatted: this.formatBytes(totalSize)
    };
  }

  /**
   * 格式化字节大小
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// 创建单例
const avatarCache = new AvatarCache();
export default avatarCache;
flarum.reg.add('ziiven-dress-up', 'common/utils/AvatarCache', avatarCache);